import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { inspectionApi } from "./api";
import { tierColor } from "./TierBadge";
import "./inspection.css";

const StartForm = ({ projects, onStart }) => {
  const [f, setF] = useState({ project_id: "", address: "", unit_label: "", bedrooms: 2, bathrooms: 1, ada_certified: false,
    fuel_burning: false, attached_garage: false, pre_1978: false, market_rent_usd: "", appliance_mode: "clear_unit", floor_level: 1 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickProject = (pid) => {
    const p = projects.find((x) => x.project_id === pid);
    setF((s) => ({ ...s, project_id: pid, address: p?.address || s.address }));
  };
  return (
    <form className="insp-card" onSubmit={(e) => { e.preventDefault(); onStart(f); }} data-testid="insp-start-form">
      <div className="insp-card__title">Start a unit inspection</div>
      <label className="insp-field">Project (optional)
        <select value={f.project_id} onChange={(e) => pickProject(e.target.value)} data-testid="insp-start-project">
          <option value="">New address / no project</option>
          {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.address || p.project_id}</option>)}
        </select>
      </label>
      <label className="insp-field">Property address
        <input required value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="1234 Main St, Dallas TX" data-testid="insp-start-address" />
      </label>
      <div className="insp-row3">
        <label className="insp-field">Unit<input value={f.unit_label} onChange={(e) => set("unit_label", e.target.value)} placeholder="e.g. 204" data-testid="insp-start-unit" /></label>
        <label className="insp-field">Bedrooms<input type="number" min="0" max="8" value={f.bedrooms} onChange={(e) => set("bedrooms", +e.target.value)} data-testid="insp-start-bedrooms" /></label>
        <label className="insp-field">Baths<input type="number" min="1" max="6" value={f.bathrooms} onChange={(e) => set("bathrooms", +e.target.value)} data-testid="insp-start-bathrooms" /></label>
      </div>
      <label className="insp-field">Owner appliance rights
        <select value={f.appliance_mode} onChange={(e) => set("appliance_mode", e.target.value)} data-testid="insp-start-appliance-mode">
          <option value="clear_unit">Mode 3 · Clear Unit / owner supplied</option>
          <option value="owner_retain">Mode 2 · Owner keeps appliances + NSPIRE safety check</option>
          <option value="procure">Mode 1 · Full DFW procurement + install</option>
        </select>
      </label>
      <label className="insp-field">Floor level (2+ adds acoustic underlayment)
        <input type="number" min="1" max="60" value={f.floor_level} onChange={(e) => set("floor_level", +e.target.value)} data-testid="insp-start-floor" />
      </label>
      <label className="insp-check"><input type="checkbox" checked={f.ada_certified} onChange={(e) => set("ada_certified", e.target.checked)} data-testid="insp-start-ada" /> ADA / accessible / elderly-ready unit</label>
      <label className="insp-check"><input type="checkbox" checked={f.fuel_burning} onChange={(e) => set("fuel_burning", e.target.checked)} data-testid="insp-start-fuel" /> Fuel-burning equipment (furnace, water heater) or attached garage</label>
      <label className="insp-check"><input type="checkbox" checked={f.attached_garage} onChange={(e) => set("attached_garage", e.target.checked)} data-testid="insp-start-garage" /> Attached garage</label>
      <label className="insp-check"><input type="checkbox" checked={f.pre_1978} onChange={(e) => set("pre_1978", e.target.checked)} data-testid="insp-start-pre1978" /> Built before 1978 (lead disclosure)</label>
      <label className="insp-field">Market rent (monthly, optional)<input type="number" min="0" value={f.market_rent_usd} onChange={(e) => set("market_rent_usd", e.target.value)} placeholder="1450" data-testid="insp-start-rent" /></label>
      <button type="submit" className="insp-btn insp-btn--primary" data-testid="insp-start-btn">Begin walkthrough →</button>
    </form>
  );
};

const RecentList = ({ rows, onOpen, state }) => (
  <div className="insp-card" data-testid="insp-recent">
    <div className="insp-card__title">Recent inspections</div>
    {state === "loading" && <div className="insp-muted" data-testid="insp-recent-loading">Loading…</div>}
    {state === "error" && <div className="insp-muted" data-testid="insp-recent-error">Couldn't load inspections. Pull to refresh in a moment.</div>}
    {state === "ok" && rows.length === 0 && <div className="insp-muted">No inspections yet.</div>}
    {rows.map((r) => (
      <button key={r.inspection_id} type="button" className="insp-recent__row" onClick={() => onOpen(r.inspection_id)} data-testid={`insp-recent-${r.inspection_id}`}>
        <span className="insp-recent__addr">{r.address}{r.unit_label ? ` · ${r.unit_label}` : ""}</span>
        <span className="insp-recent__meta">
          {r.status === "completed"
            ? <b style={{ color: tierColor(r.classification?.tier) }}>{r.classification?.label}</b>
            : <b className="insp-muted">In progress</b>} · {r.photo_count} photos · {new Date(r.started_at).toLocaleDateString()}
        </span>
      </button>
    ))}
  </div>
);

export default function InspectionHome({ embedded = false }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    inspectionApi.projects().then((p) => setProjects(Array.isArray(p) ? p : [])).catch(() => toast.error("Couldn't load projects"));
    inspectionApi.list().then((d) => { setRows(d.inspections || []); setState("ok"); }).catch(() => setState("error"));
  }, []);

  const onStart = async (f) => {
    try {
      const res = await inspectionApi.start({ ...f, project_id: f.project_id || null, market_rent_usd: f.market_rent_usd ? +f.market_rent_usd : null });
      navigate(`/inspect/${res.inspection.inspection_id}`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className={`insp-home ${embedded ? "insp-home--embedded" : ""}`} data-testid="insp-home">
      <div className="insp-home__intro">
        <div className="insp-eyebrow">Field Mode · Guided Inspection</div>
        <h1 className="insp-h1">Walk the unit. The platform sets the tier.</h1>
        <p className="insp-muted">Room by room: two wide-angle shots plus detail shots, then a condition score for every item.
          Light Touch · Mid Touch · Heavy Touch · Heavy Turn are decided automatically. Every photo gets a SHA-256 hash plus GPS and UTC stamps and goes into your evidence vault.</p>
      </div>
      <div className="insp-home__grid">
        <StartForm projects={projects} onStart={onStart} />
        <RecentList rows={rows} state={state} onOpen={(id) => navigate(`/inspect/${id}`)} />
      </div>
    </div>
  );
}
